import { Button, Skeleton } from '@mui/material';
import type { ChildCustomerOverview } from '@vy/types';
import { isEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import { useMemo, useState } from 'react';
import { AutocompleteSelect } from '../../../../../components/design/AutoCompleteSelect';
import { AddIcon } from '../../../../../components/icons/AddIcon';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { usePotentialCustomerChildren } from '../../../../../hooks/requests/use-potential-customer-children';
import { useDialog } from '../../../../../hooks/use-dialog';

type AddCustomerChildModalProps = {
  onChildSelect?: (childId: ChildCustomerOverview) => void;
  childrenToExclude?: ChildCustomerOverview[];
  childrenToAdd?: ChildCustomerOverview[];
  customerId: string;
};

export const AddCustomerChildModal = ({
  customerId,
  childrenToAdd,
  childrenToExclude,
  onChildSelect,
}: AddCustomerChildModalProps) => {
  const [selectedChild, setSelectedChild] = useState<ChildCustomerOverview>();
  const { handleClose, handleOpen, isDialogOpen } = useDialog();
  const { data, isLoading } = usePotentialCustomerChildren();

  const potentialChildCustomers = data?.potentialChildCustomers ?? [];

  const selectableChildren = useMemo(
    () => getSelectableChildren(customerId, potentialChildCustomers, childrenToExclude, childrenToAdd),
    [customerId, potentialChildCustomers, childrenToExclude, childrenToAdd],
  );

  const handleDialogSubmit = () => {
    selectedChild && onChildSelect?.(selectedChild);
    handleClose();
  };

  return isLoading ? (
    <Skeleton variant="rectangular" sx={{ width: '100px', height: '10px', borderRadius: '12px' }} />
  ) : (
    <div>
      <Button
        startIcon={<AddIcon fill="#000" />}
        onClick={handleOpen}
        disabled={isEmptyArray(selectableChildren)}
        sx={{
          color: '#000',
        }}
      >
        Add related entity
      </Button>

      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        fullWidth
        label="Add Related Entity"
        labelDescription="Add related entities and subsidiaries to this parent account"
        ctaText="Confirm"
        onSubmit={handleDialogSubmit}
      >
        <AutocompleteSelect
          options={selectableChildren}
          className="max-w-[99%] grow"
          label="Select Entity"
          getOptionLabel={(option: ChildCustomerOverview) =>
            `${option.name} ${isNotNullOrUndefined(option.externalId) ? `- ${option.externalId}` : ''}`
          }
          onChange={(_, value: ChildCustomerOverview) => setSelectedChild(value)}
          textFieldProps={{
            focused: true,
          }}
        />
      </VyDialog>
    </div>
  );
};

function getSelectableChildren(
  customerId: string,
  children: ChildCustomerOverview[],
  childrenToExclude: ChildCustomerOverview[] = [],
  childrenToAdd: ChildCustomerOverview[] = [],
) {
  const excludeIds = new Set(childrenToExclude.map((child) => child.id));
  excludeIds.add(customerId);

  const seen = new Set<string>();
  const result: ChildCustomerOverview[] = [];

  for (const child of children) {
    if (!excludeIds.has(child.id) && !seen.has(child.id)) {
      seen.add(child.id);
      result.push(child);
    }
  }

  for (const child of childrenToAdd) {
    if (!excludeIds.has(child.id) && !seen.has(child.id)) {
      seen.add(child.id);
      result.push(child);
    }
  }

  return result;
}
